// src/layouts/dashboard/index.js
import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTable";

// API
import { dashboardApi } from "api/dashboardApi";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "UGX",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format number
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};

function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getDashboardData();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setError("Failed to load dashboard data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h5">Loading dashboard data...</MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h5" color="error">
                {error}
              </MDTypography>
              <MDButton variant="gradient" color="info" onClick={fetchDashboardData} sx={{ mt: 2 }}>
                Retry
              </MDButton>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const stats = dashboardData?.statistics || {};

  // Prepare chart data for weekly claims
  const weeklyChartData = {
    labels: dashboardData?.weeklyClaims?.map((item) => item.label) || [],
    datasets: {
      label: "Claims",
      data: dashboardData?.weeklyClaims?.map((item) => item.value) || [],
    },
  };

  // Prepare chart data for monthly claims
  const monthlyChartData = {
    labels: dashboardData?.monthlyClaims?.map((item) => item.label) || [],
    datasets: {
      label: "Monthly Claims",
      data: dashboardData?.monthlyClaims?.map((item) => item.value) || [],
    },
  };

  // Prepare status distribution chart
  const statusChartData = {
    labels: dashboardData?.claimsByStatus?.map((item) => item.label) || [],
    datasets: {
      label: "Claims by Status",
      data: dashboardData?.claimsByStatus?.map((item) => item.value) || [],
    },
  };

  // Prepare top hospitals table data
  const topHospitalsColumns = [
    { Header: "Hospital Name", accessor: "name", width: "60%" },
    { Header: "Claims Count", accessor: "claims", width: "20%" },
    { Header: "Total Amount", accessor: "amount", width: "20%" },
  ];

  const topHospitalsRows =
    dashboardData?.topHospitals?.map((hospital, index) => ({
      name: hospital.label,
      claims: hospital.value,
      amount: formatCurrency(hospital.amount),
      id: index,
    })) || [];

  // Prepare recent claims table data
  const recentClaimsColumns = [
    { Header: "Reference ID", accessor: "referenceId", width: "20%" },
    { Header: "Hospital", accessor: "hospital", width: "30%" },
    { Header: "Amount", accessor: "amount", width: "15%" },
    { Header: "Status", accessor: "status", width: "15%" },
    { Header: "Date", accessor: "date", width: "20%" },
  ];

  const recentClaimsRows =
    dashboardData?.recentClaims?.map((claim) => ({
      referenceId: claim.referenceId,
      hospital: claim.hospitalName,
      amount: formatCurrency(claim.totalAmount),
      status: (
        <MDTypography
          variant="caption"
          fontWeight="medium"
          color={claim.status === "Success" ? "success" : "error"}
        >
          {claim.status}
        </MDTypography>
      ),
      date: new Date(claim.createdAt).toLocaleDateString(),
      id: claim.id,
    })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Statistics Cards Row */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="receipt"
                title="Total Claims"
                count={formatNumber(stats.totalClaims)}
                percentage={{
                  color: "success",
                  amount: `+${
                    stats.weeklyClaims > 0
                      ? Math.round((stats.weeklyClaims / stats.totalClaims) * 100)
                      : 0
                  }%`,
                  label: "than last week",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="today"
                title="Today's Claims"
                count={stats.todayClaims}
                percentage={{
                  color: stats.todayClaims > stats.yesterdayClaims ? "success" : "warning",
                  amount: `${stats.todayClaims > stats.yesterdayClaims ? "+" : ""}${
                    stats.todayClaims - (stats.yesterdayClaims || 0)
                  }`,
                  label: "vs yesterday",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="money"
                title="Total Revenue"
                count={formatCurrency(stats.totalAmount)}
                percentage={{
                  color: "success",
                  amount: `+${Math.round((stats.weeklyAmount / stats.totalAmount) * 100)}%`,
                  label: "than last week",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="trending_up"
                title="Success Rate"
                count={`${Math.round(stats.successRate)}%`}
                percentage={{
                  color: "success",
                  amount: "",
                  label: `${stats.successClaims} successful claims`,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Charts Row */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Weekly Claims"
                  description="Claims submitted this week"
                  date="Last 7 days"
                  chart={weeklyChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="Monthly Claims Trend"
                  description={
                    <>
                      (
                      <strong>
                        +
                        {stats.monthlyClaims > 0
                          ? Math.round((stats.monthlyClaims / (stats.totalClaims || 1)) * 100)
                          : 0}
                        %
                      </strong>
                      ) increase in monthly claims
                    </>
                  }
                  date="This year"
                  chart={monthlyChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="dark"
                  title="Claims by Status"
                  description="Distribution of claim statuses"
                  date="All time"
                  chart={statusChartData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Additional Statistics Row */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <ComplexStatisticsCard
                color="info"
                icon="business"
                title="Active Hospitals"
                count={stats.activeHospitals}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "healthcare providers",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <ComplexStatisticsCard
                color="warning"
                icon="computer"
                title="Active Terminals"
                count={stats.activeTerminals}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "active devices",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <ComplexStatisticsCard
                color="secondary"
                icon="assignment"
                title="Active Policies"
                count={stats.activePolicies}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "insurance policies",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <ComplexStatisticsCard
                color="error"
                icon="swap_horiz"
                title="Reversed Claims"
                count={stats.reversedClaims}
                percentage={{
                  color: "warning",
                  amount: "",
                  label: "claims reversed",
                }}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Tables Row */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    Top Performing Hospitals
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Hospitals with most claims
                  </MDTypography>
                </MDBox>
                <DataTable
                  table={{ columns: topHospitalsColumns, rows: topHospitalsRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    Recent Claims
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Latest 10 claims submitted
                  </MDTypography>
                </MDBox>
                <DataTable
                  table={{ columns: recentClaimsColumns, rows: recentClaimsRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* Monthly Summary Section */}
        <MDBox mt={4.5}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" gutterBottom>
                Monthly Performance Summary
              </MDTypography>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
              >
                <MDBox>
                  <MDTypography variant="button" color="text">
                    Total Monthly Claims: <strong>{formatNumber(stats.monthlyClaims)}</strong>
                  </MDTypography>
                </MDBox>
                <MDBox>
                  <MDTypography variant="button" color="text">
                    Monthly Revenue: <strong>{formatCurrency(stats.monthlyAmount)}</strong>
                  </MDTypography>
                </MDBox>
                <MDBox>
                  <MDTypography variant="button" color="text">
                    Average Claim Value:{" "}
                    <strong>
                      {formatCurrency(
                        stats.monthlyClaims > 0 ? stats.monthlyAmount / stats.monthlyClaims : 0
                      )}
                    </strong>
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
